import type { EpisodeOfCare } from '@medplum/fhirtypes';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import {
  deriveFamilyTherapyAttendanceFromParticipants,
  deriveGroupEncounterAttendanceFromParticipants,
  resolveIdentifiedPatientId,
  type FamilyTherapyParticipantAttendance,
} from './deriveFamilyTherapyAttendance';
import type { EncounterScrubberInput } from './types';

export type BillingServiceTypeRef = { cptCode: string };

export type { FamilyTherapyParticipantAttendance };

export function buildEncounterScrubberParticipantAttendance(input: {
  episodeOfCare?: EpisodeOfCare | null;
  notePatientRef?: string;
  attendanceRows: ReadonlyArray<{ patientId: string }>;
  attendanceByPatientId: Record<string, string | undefined>;
}): FamilyTherapyParticipantAttendance {
  const participantPatientIds = input.attendanceRows.map((row) => row.patientId);
  return {
    identifiedPatientId: resolveIdentifiedPatientId({
      episodeOfCare: input.episodeOfCare,
      notePatientRef: input.notePatientRef,
      participantPatientIds,
    }),
    attendanceByPatientId: input.attendanceByPatientId ?? {},
    participantPatientIds,
  };
}

/**
 * Map note metadata snapshot + catalog context into normalized scrubber input.
 */
export function buildEncounterScrubberInput(input: {
  metadata: NoteMetadataSnapshot;
  serviceTypes?: BillingServiceTypeRef[];
  payerCodesList?: string[];
  providerAllowedCptCodes?: string[];
  participantAttendance?: FamilyTherapyParticipantAttendance;
}): EncounterScrubberInput {
  const { metadata, serviceTypes = [] } = input;

  const currentCptCodes = (metadata.billingCodes ?? [])
    .map((row) => row.code?.trim().toUpperCase())
    .filter((c): c is string => Boolean(c) && c !== 'UNSPECIFIED');

  const providerAllowedCptCodes =
    input.providerAllowedCptCodes ??
    [...new Set(serviceTypes.map((st) => st.cptCode.trim().toUpperCase()).filter(Boolean))];

  const payerCodesList =
    input.payerCodesList ??
    (metadata.payerCodesList ?? []).map((c) => c.trim().toUpperCase());

  const familyAttendance = deriveFamilyTherapyAttendanceFromParticipants(
    input.participantAttendance,
  );
  const groupAttendance = deriveGroupEncounterAttendanceFromParticipants(
    input.participantAttendance,
  );

  return {
    durationMinutes: metadata.durationMinutes,
    currentCptCodes,
    payerCodesList,
    providerAllowedCptCodes,
    participantAttendance: input.participantAttendance,
    patientStatus: metadata.patientStatus,
    mdmLevel: metadata.mdmLevel,
    firstSessionWithClientOrCase: metadata.firstSessionWithClientOrCase,
    newTherapistFirstSessionWithClientOrCase: metadata.newTherapistFirstSessionWithClientOrCase,
    diagnosticEvaluationDocumentationPresent: metadata.diagnosticEvaluationDocumentationPresent,
    crisisDocumentationPresent: metadata.crisisDocumentationPresent,
    identifiedPatientPresent: familyAttendance.identifiedPatientPresent,
    familyParticipantPresent: familyAttendance.familyParticipantPresent,
    patientGroupEncounterAttended:
      groupAttendance ?? metadata.patientGroupEncounterAttended,
    assessmentPerformedSameDateAsSession: metadata.assessmentPerformedSameDateAsSession,
    psychotherapyAddOn: metadata.psychotherapyAddOn,
    interactiveComplexityFactorDocumented: metadata.interactiveComplexityFactorDocumented,
    payerPlanType: metadata.payerPlanType,
    payerName: metadata.payerName,
    prior96146CountForClient: metadata.prior96146CountForClient,
    legacyConsultCodeEnabled: metadata.legacyConsultCodeEnabled ?? false,
    payerAllowsConsultCodes: metadata.payerAllowsConsultCodes ?? false,
  };
}
